/**
 * GeoTiffExporter
 * 印刷用キャンバスを JPEG 圧縮の GeoTIFF (EPSG:3857) として保存するユーティリティ。
 *
 * canvas.toBlob() で得た完全な JPEG ストリーム (SOI〜EOI) を単一ストリップに
 * 格納する new-style JPEG (Compression=7) の TIFF を自前で構築する。
 * 単一ストリップは最終ストリップ扱いなので RowsPerStrip の MCU 倍数制約を
 * 受けず、ストリップが自己完結ストリームのため JPEGTables タグも不要。
 * libtiff / GDAL / QGIS が正式サポートする形式。
 *
 * キャンバスは MapLibre の描画そのまま = Web Mercator なので、ピクセル格子は
 * EPSG:3857 の投影座標に対して線形。ModelPixelScale / ModelTiepoint と
 * GeoKeyDirectory (ProjectedCSTypeGeoKey=3857) で地理参照する。
 */

import { saveAs } from 'file-saver';

// WGS84 度 → EPSG:3857 メートル
const WEB_MERC_MAX = 20037508.342789244;
const mercXm = (lng) => lng / 180 * WEB_MERC_MAX;
const mercYm = (lat) => 6378137 * Math.log(Math.tan(Math.PI / 4 + lat * Math.PI / 360));

// TIFF フィールド型
const T_ASCII    = 2;  // 1B
const T_SHORT    = 3;  // 2B
const T_LONG     = 4;  // 4B
const T_RATIONAL = 5;  // 8B (LONG 分子 + LONG 分母)
const T_DOUBLE   = 12; // 8B
const TYPE_SIZE = { [T_ASCII]: 1, [T_SHORT]: 2, [T_LONG]: 4, [T_RATIONAL]: 8, [T_DOUBLE]: 8 };

// canvas を JPEG バイト列へ変換 (GeoPdfExporter と同じ実装)
const canvasToJpegBytes = (canvas, quality) => new Promise((resolve, reject) => {
	canvas.toBlob((blob) => {
		if (!blob) {
			reject(new Error('canvas.toBlob returned null'));
			return;
		}
		blob.arrayBuffer()
			.then((buf) => resolve(new Uint8Array(buf)))
			.catch(reject);
	}, 'image/jpeg', quality);
});

/**
 * JPEG の SOF0/1/2 セグメントから Y 成分のサブサンプリング係数を実測する。
 * ブラウザ・品質設定により 4:2:0 / 4:4:4 等が変わるため、YCbCrSubSampling
 * タグを決め打ちにすると libtiff 系リーダーが不一致警告を出す。
 * @returns {{ hSamp: number, vSamp: number }}
 */
const parseJpegSubsampling = (bytes) => {
	if (bytes[0] !== 0xFF || bytes[1] !== 0xD8) throw new Error('not a JPEG stream');
	let pos = 2;
	while (pos + 4 <= bytes.length) {
		if (bytes[pos] !== 0xFF) throw new Error('broken JPEG marker structure');
		const marker = bytes[pos + 1];
		if (marker === 0xDA) break; // SOS: 以降は画像データ
		const segLen = (bytes[pos + 2] << 8) | bytes[pos + 3];
		if (marker === 0xC0 || marker === 0xC1 || marker === 0xC2) { // SOF0/1/2
			// セグメント: 精度(1) 高さ(2) 幅(2) 成分数(1) [id(1) サンプリング(1) 量子化表(1)]×n
			const numComponents = bytes[pos + 9];
			for (let c = 0; c < numComponents; c++) {
				const compOff = pos + 10 + c * 3;
				if (bytes[compOff] === 1) { // Y 成分
					const s = bytes[compOff + 1];
					return { hSamp: s >> 4, vSamp: s & 0x0F };
				}
			}
			throw new Error('JPEG SOF has no luminance component');
		}
		pos += 2 + segLen;
	}
	// グレースケール等で SOF が見つからない場合は 1x1 とみなす
	return { hSamp: 1, vSamp: 1 };
};

/**
 * JPEG圧縮 GeoTIFF の ArrayBuffer を構築する。
 * レイアウト: ヘッダ8B → IFD → 外部値領域 (2B整列) → JPEGデータ (偶数オフセット)
 */
const buildGeoTiff = ({ jpegBytes, width, height, pixelScale, tiepoint, dpi }) => {
	const { hSamp, vSamp } = parseJpegSubsampling(jpegBytes);

	// GeoTIFF キー: GTModelType=Projected(1), GTRasterType=PixelIsArea(1), ProjectedCSType=3857
	const geoKeyDirectory = [1, 1, 0, 3, 1024, 0, 1, 1, 1025, 0, 1, 1, 3072, 0, 1, 3857];
	const software = 'CollaboMap';

	// タグ定義 (ID 昇順)。values: 数値配列 / 文字列 / RATIONAL は [分子, 分母] の配列
	const tags = [
		{ id: 256,   type: T_LONG,     values: [width] },                 // ImageWidth
		{ id: 257,   type: T_LONG,     values: [height] },                // ImageLength
		{ id: 258,   type: T_SHORT,    values: [8, 8, 8] },               // BitsPerSample
		{ id: 259,   type: T_SHORT,    values: [7] },                     // Compression: new-style JPEG
		{ id: 262,   type: T_SHORT,    values: [6] },                     // Photometric: YCbCr
		{ id: 273,   type: T_LONG,     values: [0] },                     // StripOffsets (後で確定)
		{ id: 277,   type: T_SHORT,    values: [3] },                     // SamplesPerPixel
		{ id: 278,   type: T_LONG,     values: [height] },                // RowsPerStrip: 単一ストリップ
		{ id: 279,   type: T_LONG,     values: [jpegBytes.length] },      // StripByteCounts
		{ id: 282,   type: T_RATIONAL, values: [[dpi, 1]] },              // XResolution
		{ id: 283,   type: T_RATIONAL, values: [[dpi, 1]] },              // YResolution
		{ id: 284,   type: T_SHORT,    values: [1] },                     // PlanarConfiguration
		{ id: 296,   type: T_SHORT,    values: [2] },                     // ResolutionUnit: inch
		{ id: 305,   type: T_ASCII,    values: software },                // Software
		{ id: 530,   type: T_SHORT,    values: [hSamp, vSamp] },          // YCbCrSubSampling (SOF実測)
		{ id: 33550, type: T_DOUBLE,   values: pixelScale },              // ModelPixelScaleTag
		{ id: 33922, type: T_DOUBLE,   values: tiepoint },                // ModelTiepointTag
		{ id: 34735, type: T_SHORT,    values: geoKeyDirectory },         // GeoKeyDirectoryTag
	];

	// レイアウト計算: 値が4バイト超のタグは外部領域にオフセット参照で置く
	const tagCount = (tag) => tag.type === T_ASCII ? tag.values.length + 1 : tag.values.length;
	const tagByteLen = (tag) => TYPE_SIZE[tag.type] * tagCount(tag);

	const ifdOffset = 8;
	const ifdSize = 2 + tags.length * 12 + 4;
	let extOffset = ifdOffset + ifdSize;
	for (const tag of tags) {
		if (tagByteLen(tag) > 4) {
			if (extOffset % 2) extOffset++;
			tag.extOffset = extOffset;
			extOffset += tagByteLen(tag);
		}
	}
	let jpegOffset = extOffset;
	if (jpegOffset % 2) jpegOffset++;

	tags.find(t => t.id === 273).values = [jpegOffset]; // StripOffsets 確定

	const buffer = new ArrayBuffer(jpegOffset + jpegBytes.length);
	const view = new DataView(buffer);
	const u8 = new Uint8Array(buffer);

	// 値の書き込み (リトルエンディアン)
	const writeValues = (tag, offset) => {
		if (tag.type === T_ASCII) {
			for (let i = 0; i < tag.values.length; i++) u8[offset + i] = tag.values.charCodeAt(i) & 0x7F;
			u8[offset + tag.values.length] = 0; // NUL 終端
		} else if (tag.type === T_RATIONAL) {
			tag.values.forEach(([num, den], i) => {
				view.setUint32(offset + i * 8, num, true);
				view.setUint32(offset + i * 8 + 4, den, true);
			});
		} else if (tag.type === T_DOUBLE) {
			tag.values.forEach((v, i) => view.setFloat64(offset + i * 8, v, true));
		} else if (tag.type === T_SHORT) {
			tag.values.forEach((v, i) => view.setUint16(offset + i * 2, v, true));
		} else { // T_LONG
			tag.values.forEach((v, i) => view.setUint32(offset + i * 4, v, true));
		}
	};

	// TIFF ヘッダ
	u8[0] = 0x49; u8[1] = 0x49;          // "II" リトルエンディアン
	view.setUint16(2, 42, true);         // マジックナンバー
	view.setUint32(4, ifdOffset, true);  // 最初の IFD へのオフセット

	// IFD
	view.setUint16(ifdOffset, tags.length, true);
	tags.forEach((tag, i) => {
		const entry = ifdOffset + 2 + i * 12;
		view.setUint16(entry, tag.id, true);
		view.setUint16(entry + 2, tag.type, true);
		view.setUint32(entry + 4, tagCount(tag), true);
		if (tag.extOffset != null) {
			view.setUint32(entry + 8, tag.extOffset, true);
			writeValues(tag, tag.extOffset);
		} else {
			writeValues(tag, entry + 8); // 4バイト以内はインライン格納
		}
	});
	view.setUint32(ifdOffset + 2 + tags.length * 12, 0, true); // 次の IFD なし

	u8.set(jpegBytes, jpegOffset);
	return buffer;
};

/**
 * キャンバスを JPEG 圧縮 GeoTIFF として保存する。
 *
 * @param {object} options
 * @param {HTMLCanvasElement} options.canvas   印刷用キャンバス（全面が bounds に対応していること）
 * @param {{ west, east, south, north }} options.bounds  キャンバス全体の地理範囲 (WGS84 度)
 * @param {string} options.fileName  保存ファイル名
 * @param {number} [options.jpegQuality=0.8]
 * @param {number} [options.dpi=300]
 */
export async function exportGeoTiff({ canvas, bounds, fileName, jpegQuality = 0.8, dpi = 300 }) {
	const jpegBytes = await canvasToJpegBytes(canvas, jpegQuality);

	const xW = mercXm(bounds.west);
	const xE = mercXm(bounds.east);
	const yN = mercYm(bounds.north);
	const yS = mercYm(bounds.south);

	const buffer = buildGeoTiff({
		jpegBytes,
		width:  canvas.width,
		height: canvas.height,
		// ラスタ左上ピクセル (0,0) を投影座標 (xW, yN) に対応付け、スケールは両方正値
		tiepoint:   [0, 0, 0, xW, yN, 0],
		pixelScale: [(xE - xW) / canvas.width, (yN - yS) / canvas.height, 0],
		dpi,
	});

	saveAs(new Blob([buffer], { type: 'image/tiff' }), fileName);
}

// Node での単体検証用に内部関数も公開する
export { buildGeoTiff, parseJpegSubsampling, mercXm, mercYm };
